import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import type { Tournament } from '../../../stores/tournament/types';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { formatTime } from '../../../utils/time';

interface UseTournamentWidgetDataProps {
  event: Tournament;
}

export function useTournamentWidgetData(props: UseTournamentWidgetDataProps) {
  const { event } = props;
  const { coinTypeStore, uiStore, tournamentStore } = useStores();
  const { status, leaderboards, playerStatus } = event;
  const coinType = coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
  const leaderboard = leaderboards[coinType];

  const isTournamentUpcoming = status === 'upcoming';
  const isTournamentOngoing = status === 'ongoing';
  const isTournamentStartedForPlayer = isTournamentOngoing && playerStatus === 'started';

  const { startDate, endDate, widgetImageUrl, maxPositionDisplay } = event.tournamentGroup;
  const showTimer = isTournamentUpcoming || isTournamentOngoing;

  const positionValue = useMemo(() => {
    if (!leaderboard || !isTournamentStartedForPlayer) {
      return null;
    }
    const playerPosition = leaderboard.player.position + 1;
    if (playerPosition > maxPositionDisplay) {
      return `${maxPositionDisplay}+`;
    }
    return `#${playerPosition}`;
  }, [isTournamentStartedForPlayer, leaderboard, maxPositionDisplay]);

  const onTimerEnd = useCallback(() => {
    // Random delay between 1 and 5 seconds to reduce server load
    const randomDelayMS = (Math.floor(Math.random() * 5) + 1) * 1000;

    setTimeout(() => {
      void tournamentStore.fetch();
    }, randomDelayMS);
  }, [tournamentStore]);

  const handleClick = useCallback(() => {
    let timeLeftAnalytics;
    if (showTimer) {
      const destinationTime = isTournamentUpcoming ? startDate : endDate;
      const secondsToDate = dayjs(destinationTime).diff(dayjs(), 'seconds');
      const timer = formatTime(secondsToDate, 's');
      timeLeftAnalytics = { time_left: timer };
    }

    tournamentStore.handleAnalytics({
      event_action: 'impression',
      params: {
        triggered_by: 'click',
        click_location: 'widget',
        ...(timeLeftAnalytics ? timeLeftAnalytics : {}),
      },
    });

    if (isTournamentOngoing) {
      void tournamentStore.fetch();
    }

    uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: {
        shown_by: 'widget_click',
      },
    });
  }, [endDate, isTournamentOngoing, isTournamentUpcoming, showTimer, startDate, tournamentStore, uiStore]);

  return {
    leaderboard,
    onTimerEnd,
    widgetImageUrl,
    positionValue,
    handleClick,
    startDate,
    endDate,
    isTournamentUpcoming,
  };
}
